import { create } from 'zustand';

/**
 * Which people are ticked in each Review folder — the "Test selected" bar.
 *
 * Module-scope zustand (CLAUDE.md): the ticks must survive the parent re-render
 * every SSE `artifact:updated` causes, and a folder unmounts when it closes.
 * Keyed by play + group ({@link selectionKey}) so a tick can never leak into
 * another play or another group — a person is tested from the group the rep
 * ticked them in. `PlayFlowShell` resets this with the other play-scoped stores.
 */
interface ReviewSelectionState {
  /** Ticked member ids per {@link selectionKey}, in tick order. */
  byKey: Record<string, string[]>;
}

/** Exported per the store convention — reset idiom is `useReviewSelectionStore.setState(initialState, true)`. */
export const initialState: ReviewSelectionState = { byKey: {} };

export const useReviewSelectionStore = create<ReviewSelectionState>(() => ({ ...initialState }));

export function selectionKey(playId: string, groupKey: string): string {
  return `${playId}:${groupKey}`;
}

/** Ticks or unticks one person. */
export function toggleSelected(playId: string, groupKey: string, memberId: string): void {
  const key = selectionKey(playId, groupKey);
  useReviewSelectionStore.setState((state) => {
    const current = state.byKey[key] ?? [];
    const next = current.includes(memberId) ? current.filter((id) => id !== memberId) : [...current, memberId];
    return { byKey: { ...state.byKey, [key]: next } };
  });
}

/** Replaces one group's ticks (select all / deselect all). */
export function setGroupSelection(playId: string, groupKey: string, memberIds: readonly string[]): void {
  const key = selectionKey(playId, groupKey);
  useReviewSelectionStore.setState((state) => ({ byKey: { ...state.byKey, [key]: [...memberIds] } }));
}

/** Empties one group's ticks (Clear, or after a test started). */
export function clearGroupSelection(playId: string, groupKey: string): void {
  const key = selectionKey(playId, groupKey);
  useReviewSelectionStore.setState((state) => {
    const { [key]: _cleared, ...rest } = state.byKey;
    return { byKey: rest };
  });
}

/** The play-scoped reset `PlayFlowShell` performs. */
export function clearReviewSelection(): void {
  useReviewSelectionStore.setState({ ...initialState });
}
